import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Chip,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import api from '../api';
import MonthSummaryBar from './MonthSummaryBar';
import InvoiceCreateDialog from './invoices/InvoiceCreateDialog';
import PageHeader from './common/PageHeader';
import PageContainer from './common/PageContainer';

export default function InvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [month, setMonth] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [total, setTotal] = useState(0);

  const [statusDialog, setStatusDialog] = useState({
    open: false,
    id: null,
    current: '',
  });
  const [newStatus, setNewStatus] = useState('');

  // ===== Invoice editor =====
  const [openEditor, setOpenEditor] = useState(false);
  const [editorMode, setEditorMode] = useState('create'); // create | edit
  const [editorCustomer, setEditorCustomer] = useState(null);
  const [editorInitialData, setEditorInitialData] = useState(null);

  /* =========================
   * load months
   * ========================= */
  const loadAvailableMonths = async () => {
    try {
      const res = await api.get('/api/invoices/months');
      const months = res.data || [];
      setAvailableMonths(months);
      if (months.length > 0) {
        setMonth(dayjs(months[0]));
      }
    } catch (e) {
      console.error('[loadAvailableMonths]', e);
    }
  };

  /* =========================
   * load invoices
   * ========================= */
  const loadInvoices = async () => {
    if (!month) return;
    try {
      const ym = month.format('YYYY-MM');
      const res = await api.get('/api/invoices', { params: { month: ym } });
      setInvoices(res.data.data || []);
      setTotal(res.data.totalAmount || 0);
    } catch (e) {
      console.error('[loadInvoices]', e);
    }
  };

  useEffect(() => {
    loadAvailableMonths();
  }, []);

  useEffect(() => {
    loadInvoices();
  }, [month]);

  /* =========================
   * open editor (EDIT)
   * ========================= */
  const openEditorForEdit = async (invoiceId) => {
    try {
      // 請求書 + 明細
      const invRes = await api.get(`/api/invoices/${invoiceId}`);
      const invoice = invRes.data;

      // 顧客
      let customer = null;
      try {
        const c = await api.get(`/api/customers/${invoice.customerId}`);
        customer = c.data;
      } catch {
        // fallback: 一覧から探す
        const cl = await api.get('/api/customers');
        const list = cl.data?.customers || cl.data || [];
        customer = list.find(
          (x) => String(x.id) === String(invoice.customerId)
        );
      }

      setEditorMode('edit');
      setEditorInitialData(invoice);
      setEditorCustomer(customer || null);
      setOpenEditor(true);
    } catch (e) {
      console.error('[openEditorForEdit]', e);
      alert('請求書データの取得に失敗しました');
    }
  };

  /* =========================
   * status change
   * ========================= */
  const handleStatusChange = async () => {
    await api.patch(`/api/invoices/${statusDialog.id}/status`, {
      status: newStatus,
    });
    setStatusDialog({ open: false, id: null, current: '' });
    setNewStatus('');
    loadInvoices();
  };

  /* =========================
   * pdf
   * ========================= */
  const handleGeneratePdf = async (id) => {
    const res = await api.get(`/api/invoices/${id}/pdf`);
    const { downloadUrl, fileName } = res.data || {};
    if (!downloadUrl) return alert('PDF生成に失敗しました');

    const pdf = await api.get(downloadUrl, { responseType: 'blob' });
    const url = window.URL.createObjectURL(pdf.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName || `invoice_${id}.pdf`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  /* =========================
   * columns（UI変更なし）
   * ========================= */
  const columns = [
    {
      field: 'invoice_no',
      headerName: '請求番号',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {p.value || '-'}
        </Box>
      ),
    },
    {
      field: 'projectTitle',
      headerName: '案件名',
      width: 180,
      headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {p.value || '-'}
        </Box>
      ),
    },
    {
      field: 'customerName',
      headerName: '顧客名',
      width: 160,
      headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {p.value || '-'}
        </Box>
      ),
    },
    {
      field: 'amount',
      headerName: '請求金額',
      width: 140,
      align: 'right',
      headerAlign: 'center',
      renderCell: (p) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            height: '100%',
            fontWeight: 'bold',
          }}
        >
          ¥{(p.row.amount || 0).toLocaleString()}
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'ステータス',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => {
        const color =
          p.value === '入金済'
            ? 'success'
            : p.value === '発行済'
              ? 'info'
              : 'default';
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip label={p.value || '未発行'} color={color} />
          </Box>
        );
      },
    },
    {
      field: 'issueDate',
      headerName: '発行日',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {p.value ? dayjs(p.value).format('YYYY/MM/DD') : '-'}
        </Box>
      ),
    },
    {
      field: 'dueDate',
      headerName: '支払期限',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          {p.value ? dayjs(p.value).format('YYYY/MM/DD') : '-'}
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: '操作',
      width: 280,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            gap: 1,
          }}
        >
          <Button
            size="small"
            variant="outlined"
            onClick={() => openEditorForEdit(p.row.id)}
          >
            編集
          </Button>

          <Button
            size="small"
            variant="contained"
            color="info"
            onClick={() => handleGeneratePdf(p.row.id)}
          >
            PDF
          </Button>

          <Button
            size="small"
            variant="text"
            onClick={() =>
              setStatusDialog({
                open: true,
                id: p.row.id,
                current: p.row.status,
              })
            }
          >
            状態
          </Button>
        </Box>
      ),
    },
  ];

  const baseMonth = month ? month.format('YYYY-MM') : '';

  return (
    <PageContainer>
      <PageHeader title="請求書管理" />

      {/* 月選択 */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>対象月</InputLabel>
          <Select
            label="対象月"
            value={month ? month.format('YYYY-MM') : ''}
            onChange={(e) => setMonth(dayjs(e.target.value))}
          >
            {availableMonths.map((m) => (
              <MenuItem key={m} value={m}>
                {dayjs(m).format('YYYY年MM月')}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography>
          合計請求額：<strong> ¥{total.toLocaleString()}</strong>
        </Typography>

        <Box sx={{ flexGrow: 1 }} />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ ml: 1 }}
        >
          ※ 請求書は「クライアント管理」または「案件管理」から発行してください
        </Typography>
      </Box>

      {baseMonth && <MonthSummaryBar type="invoices" month={baseMonth} />}

      <DataGrid
        rows={invoices}
        columns={columns}
        autoHeight
        pageSizeOptions={[20]}
        disableRowSelectionOnClick
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
          },
        }}
      />

      {/* Invoice editor */}
      {openEditor && (
        <InvoiceCreateDialog
          open={openEditor}
          mode={editorMode}
          customer={editorCustomer}
          initialData={editorInitialData}
          onClose={() => {
            setOpenEditor(false);
            setEditorCustomer(null);
            setEditorInitialData(null);
            setEditorMode('create');
          }}
          onSubmit={() => {
            setOpenEditor(false);
            setEditorCustomer(null);
            setEditorInitialData(null);
            setEditorMode('create');
            loadInvoices();
          }}
        />
      )}

      {/* ステータス変更 */}
      <Dialog
        open={statusDialog.open}
        onClose={() =>
          setStatusDialog({ open: false, id: null, current: '' })
        }
      >
        <DialogTitle>ステータス変更</DialogTitle>
        <DialogContent>
          <FormControl fullWidth size="small">
            <InputLabel>新しいステータス</InputLabel>
            <Select
              label="新しいステータス"
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
            >
              <MenuItem value="未発行">未発行</MenuItem>
              <MenuItem value="発行済">発行済</MenuItem>
              <MenuItem value="入金済">入金済</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ mt: 1 }}>
            現在：{statusDialog.current}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setStatusDialog({ open: false, id: null, current: '' })
            }
          >
            キャンセル
          </Button>
          <Button
            variant="contained"
            disabled={!newStatus}
            onClick={handleStatusChange}
          >
            更新
          </Button>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
}
